import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector, shallowEqual } from 'react-redux'
import path from 'ramda/es/path'
import cn from 'classnames/bind'

import Loader from 'app/common/components/Loader'
import StudentsSelector from 'app/components/StudentsSelector'
import CommonPage from 'app/common/templates/CommonPage'
import * as services from 'app/common/constants/services'
import { getIsAppConnected } from 'app/selectors/applications'
import {
  useAcquiringSettingsFetch,
  useAcquiringSettingsAvailability,
  useAcquiringSettingsLoading,
} from 'app/dataProviders/acquiring/settings/hooks'
import {
  useTransferAvailability,
  useTransferFetch,
  useTransferLoading,
} from 'app/dataProviders/transfer/hooks'
import Sticky from 'app/common/templates-next/sticky'
// import OldSticky, { withStickyRows } from 'app/common/components/Sticky'

import { selectIsCafeteriaEnabled, selectIsGlolimeEnabled, selectIsItekoEnabled } from './selectors'
import Navigation from './Navigation'
import NavigationSchoolType from './Navigation/NavigationSchoolType'
import RestrictMessage from './RestrictMessage'
import getBaseUrl from './getBaseUrl'
import RestrictIdentifier from '../RestrictIdentifier'
import AccountButtons from './AccountButtons'
import useRedirectToStudent from './useRedirectToStudent'

import styles from './CafeteriaPage.scss'

type Props = {
  children: Node
  params: {
    id: string
    schoolType: string
  }
  location: {
    pathname: string
  }
  router: {
    push: (path: string) => void
  }
}
const cx = cn.bind(styles)

const CafeteriaPage = ({
  children,
  params: { id, schoolType },
  location: { pathname },
  router: { push },
}: Props) => {
  const isCafeteria = useSelector((state) => selectIsCafeteriaEnabled(state, id))
  const isGlolime = useSelector((state) => selectIsGlolimeEnabled(state, id))
  const isLoadingOpts = useSelector((state: any) => state.apps.opts.loading, shallowEqual)
  const isLoadingStudents = useSelector((state: any) => state.students.loading, shallowEqual)
  const isSubscribed = useSelector((state) =>
    getIsAppConnected(state, { id, app: services.APP_CAFETERIA }),
  )
  const isIteko = useSelector((state) => selectIsItekoEnabled(state, id))
  const noIdentifier = useSelector(path(['students', 'data', id, 'appsBlocked']), shallowEqual)

  const restrictCafe = !isCafeteria || isGlolime
  const { t } = useTranslation('cafeteria')

  const baseURL = getBaseUrl({ id, schoolType })
  const baseUrlGeneral = getBaseUrl({ id })
  const baseUrlCamp = getBaseUrl({ id, schoolType: 'camp' })

  const hasMaps = useRedirectToStudent(schoolType !== 'general', id, (idForRedirect) => {
    push(getBaseUrl({ id: String(idForRedirect), schoolType }))
  })

  const handleReplenishClick = useCallback(() => {
    if (schoolType === 'camp') {
      push(`${baseURL}/fund/refill/child/camp`)
      return
    }
    push(`${baseURL}/fund/refill`)
  }, [push, baseURL, schoolType])

  const handleTransferClick = useCallback(() => {
    push(`${baseURL}/fund/transfer`)
  }, [push, baseURL])

  useAcquiringSettingsFetch()
  const showRefill = useAcquiringSettingsAvailability()
  const loadingRefill = useAcquiringSettingsLoading()

  useTransferFetch()
  const transferAvailability = useTransferAvailability()
  const showTransfer = transferAvailability && schoolType !== 'camp'
  const loadingTransfer = useTransferLoading()

  if (isLoadingOpts || isLoadingStudents) {
    return <Loader />
  }

  const navigationHeader = (
    <>
      {hasMaps && (
        <NavigationSchoolType baseUrlGeneral={baseUrlGeneral} baseUrlCamp={baseUrlCamp} />
      )}
      <div className={styles.navigationsTopBar}>
        <StudentsSelector camp={schoolType === 'camp'} />
        <AccountButtons
          onReplenishClick={handleReplenishClick}
          onTransferClick={handleTransferClick}
          isRefillAvailable={showRefill}
          isTransferAvailable={showTransfer}
          loading={isLoadingOpts || isLoadingStudents || loadingRefill || loadingTransfer}
        />
      </div>
      <Navigation
        studentID={id}
        pathname={pathname}
        push={push}
        baseURL={baseURL}
        isSubscribed={isSubscribed}
        isIteko={isIteko}
        isLoading={isLoadingOpts || isLoadingStudents}
        isCamp={schoolType === 'camp'}
      />
    </>
  )

  return (
    <CommonPage header={t(`subs:${services.APP_CAFETERIA}`)}>
      <Sticky>
        {(ref, _, shadow) => {
          const rootClassName = cx(styles.header, { borderShadowBottom: shadow })
          return (
            <div ref={ref} className={rootClassName}>
              {navigationHeader}
            </div>
          )
        }}
      </Sticky>
      {noIdentifier && <RestrictIdentifier studentId={id} />}
      {restrictCafe && <RestrictMessage isGlolime={isGlolime} />}
      {!restrictCafe && !noIdentifier && children}
    </CommonPage>
  )
}

export default CafeteriaPage
